import { Component, Prop, State, h } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const MEDIA_BREAKPOINT_PX = 960;

/**
 * Hero header component for SignNow brand main sections
 *
 * @product signnow
 * @source intro
 * @version 1.0.1
 * @category hero
 * @contentConfig Hero header component for main landing sections
 * @contentConfig Hero header component for main landing sections
 * @constant HERO_HEADER_LIST_ITEM_1 - "Quick to start" - First feature list item
 * @constant HERO_HEADER_LIST_ITEM_2 - "Easy-to-use" - Second feature list item
 * @constant HERO_HEADER_LIST_ITEM_3 - "24/7 support" - Third feature list item
 * @constant HERO_HEADER_FORM_PLACEHOLDER - "Enter your email" - Form input placeholder
 * @constant HERO_HEADER_FORM_SUBMIT - "Start your free trial" - Form submit button text
 * @constant HERO_HEADER_FORM_NOTE - "No credit card required" - Form note text
 * @prop {object} values - {h1, subheading} - Object containing h1 and subheading for the component
 * @slot heading - {required}, <me-heading variant="1"> - Main hero heading
 * @slot description - <me-list listType="icon"> - Hero feature list with icons
 * @slot media - {required} - Hero image or illustration
 * @slot action-primary - {required} - Primary action form or button
 */

@Component({
  tag: 'sn-hero-header-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNHeroHeaderType1 {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * Main heading text
   */
  @Prop({ attribute: 'h1', reflect: true }) h1?: string;

  /**
   * Subheading text
   */
  @Prop({ attribute: 'subheading', reflect: true }) subheading?: string;

  /**
   * @dynamic
   * @type {object}
   * @property {string} h1 - Main heading
   * @property {string} subheading - Subheading text
   * @required h1
   * @required subheading
   */
  @Prop({ mutable: true }) values?:
    | {
        h1?: string;
        subheading?: string;
      }
    | string;

  /**
   * HTML tag for the heading element (h1, h2, h3, h4, h5, h6)
   * @default "h1"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h1';

  /**
   * True when viewport is desktop (>= MEDIA_BREAKPOINT_PX). Media slot is not rendered on mobile to avoid loading the image.
   */
  @State() showMedia = false;

  private mediaQueryList?: MediaQueryList;
  private mediaQueryListener?: () => void;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
    this.values = parseJsonProp(this.values);
  }

  componentDidLoad() {
    const query = `(min-width: ${MEDIA_BREAKPOINT_PX}px)`;
    this.mediaQueryList = window.matchMedia(query);
    this.mediaQueryListener = () => {
      this.showMedia = this.mediaQueryList!.matches;
    };
    this.mediaQueryListener();
    this.mediaQueryList.addEventListener('change', this.mediaQueryListener);
  }

  disconnectedCallback() {
    if (this.mediaQueryList && this.mediaQueryListener) {
      this.mediaQueryList.removeEventListener('change', this.mediaQueryListener);
    }
  }

  render() {
    // Use individual props if available, otherwise fall back to values object
    const heading = this.h1 ?? (this.values && typeof this.values !== 'string' ? this.values.h1 : undefined);

    return (
      <header role="banner">
        <hero-header-type-1 class="sn-hero-header-type-1" contentPosition="right">
          <me-heading slot="heading" variant="1" tag={this.headingTag}>
            {heading}
          </me-heading>
          <me-list
            slot="description"
            items={[
              { text: getConstant(this.constants, 'HERO_HEADER_LIST_ITEM_1', 'Quick to start'), icon: 'arrows--chevron-down-circle' },
              { text: getConstant(this.constants, 'HERO_HEADER_LIST_ITEM_2', 'Easy-to-use'), icon: 'arrows--chevron-down-circle' },
              { text: getConstant(this.constants, 'HERO_HEADER_LIST_ITEM_3', '24/7 support'), icon: 'arrows--chevron-down-circle' },
            ]}
            listType="icon"
            layout="inline"
            iconInline={true}
          ></me-list>
          <div slot="media" class="sn-hero-header-type-1__media">
            {this.showMedia && (
              <me-picture
                lazy={false}
                fetchPriority="high"
                srcSet="https://marketing-static.signnow.com/353/images/e-signature/group-frame-hero.png 1x, https://marketing-static.signnow.com/353/images/e-signature/group-frame-hero@2x.png 2x"
                webpSrcSet="https://marketing-static.signnow.com/353/images/e-signature/group-frame-hero.webp 1x, https://marketing-static.signnow.com/353/images/e-signature/group-frame-hero@2x.webp 2x"
                aspectRatio="1.2"
                alt="E-signature frame illustration"
              />
            )}
          </div>
          <form-free-trial-type-1            constants={this.constants as Record<string, string>}            slot="action-primary"
            button-variant="primary"
            mobile-layout="stacked"
            note={getConstant(this.constants, 'HERO_HEADER_FORM_NOTE', 'No credit card required')}
            submitText={getConstant(this.constants, 'HERO_HEADER_FORM_SUBMIT', 'Start your free trial')}
          ></form-free-trial-type-1>
        </hero-header-type-1>
      </header>
    );
  }
}
